import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Download,
  FileText,
  Link2,
  Loader2,
  MailCheck,
  Paperclip,
  Plus,
  Save,
  ShieldCheck,
  Upload,
  UserRound,
  XCircle,
} from "lucide-react";
import { equipmentAPI } from "../../../../services/equipmentAPI";

const ROLE_LABELS = {
  any: "Any Member",
  pe: "PE",
  qaqc: "QA/QC",
  field: "Field",
  third_party: "3rd Party",
};

const ROLE_GROUPS = {
  pe: new Set(["project_engineer"]),
  qaqc: new Set(["qa_qc_manager", "inspector"]),
  field: new Set([
    "foreman",
    "general_foreman",
    "supervisor",
    "site_supervisor",
    "operations_manager",
    "project_manager",
  ]),
  third_party: new Set(["third_party"]),
};

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-50 disabled:text-gray-500";

const AUTOSAVE_DELAY_MS = 1000;

const sameDraftValue = (left, right) =>
  JSON.stringify(left || { fields: {} }) === JSON.stringify(right || { fields: {} });

const savedRowDraft = (serverDetail, rowId, fallback) => {
  for (const section of serverDetail?.sections || []) {
    const row = (section.rows || []).find((item) => Number(item.id) === Number(rowId));
    if (row) return { fields: { ...(row.response?.fields || {}) } };
  }
  return fallback || { fields: {} };
};

const mergeAutosaveDetail = (current, serverDetail, savedRowIds) => {
  if (!current || !serverDetail) return current;
  const serverSections = new Map(
    (serverDetail.sections || []).map((section) => [Number(section.id), section]),
  );
  const savedIds = new Set([...savedRowIds].map(Number));
  return {
    ...current,
    status: serverDetail.status ?? current.status,
    updated_at: serverDetail.updated_at ?? current.updated_at,
    stage_progress: serverDetail.stage_progress ?? current.stage_progress,
    progress_percent: serverDetail.progress_percent ?? current.progress_percent,
    completion_percent: serverDetail.completion_percent ?? current.completion_percent,
    current_stage_completion_percent:
      serverDetail.current_stage_completion_percent ?? current.current_stage_completion_percent,
    current_stage_required: serverDetail.current_stage_required ?? current.current_stage_required,
    current_stage_complete: serverDetail.current_stage_complete ?? current.current_stage_complete,
    overall_completion_percent:
      serverDetail.overall_completion_percent ?? current.overall_completion_percent,
    completion_required: serverDetail.completion_required ?? current.completion_required,
    completion_complete: serverDetail.completion_complete ?? current.completion_complete,
    verification_percent: serverDetail.verification_percent ?? current.verification_percent,
    sections: (current.sections || []).map((section) => {
      const serverSection = serverSections.get(Number(section.id));
      if (!serverSection) return section;
      const serverRows = new Map(
        (serverSection.rows || []).map((row) => [Number(row.id), row]),
      );
      return {
        ...section,
        status: serverSection.status ?? section.status,
        progress: serverSection.progress ?? section.progress,
        rows: (section.rows || []).map((row) => {
          if (!savedIds.has(Number(row.id))) return row;
          const serverRow = serverRows.get(Number(row.id));
          if (!serverRow) return row;
          return {
            ...row,
            status: serverRow.status ?? row.status,
            response: serverRow.response ?? row.response,
            answered_by_user_id:
              serverRow.answered_by_user_id ?? row.answered_by_user_id,
            answered_by_name: serverRow.answered_by_name ?? row.answered_by_name,
            answered_at: serverRow.answered_at ?? row.answered_at,
            updated_at: serverRow.updated_at ?? row.updated_at,
          };
        }),
      };
    }),
  };
};

const apiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};

const safeColor = (value, fallback = "#E5E7EB") =>
  /^#[0-9A-Fa-f]{6}$/.test(String(value || "")) ? value : fallback;

const tint = (hex, alpha = 0.13) => {
  const clean = safeColor(hex).slice(1);
  const red = parseInt(clean.slice(0, 2), 16);
  const green = parseInt(clean.slice(2, 4), 16);
  const blue = parseInt(clean.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};


const toneStyle = (hex, { bgAlpha = 0.12, borderAlpha = 0.32 } = {}) => ({
  backgroundColor: tint(hex, bgAlpha),
  borderColor: tint(hex, borderAlpha),
});

const layeredToneStyle = (hex, { from = 0.18, to = 0.08, borderAlpha = 0.32 } = {}) => ({
  background: `linear-gradient(180deg, ${tint(hex, from)} 0%, ${tint(hex, to)} 100%)`,
  borderColor: tint(hex, borderAlpha),
});

const fmtTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const StatusPill = ({ status }) => {
  const styles = {
    draft: "bg-gray-100 text-gray-700",
    in_progress: "bg-blue-100 text-blue-800",
    submitted: "bg-amber-100 text-amber-800",
    verified: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    archived: "bg-gray-100 text-gray-500",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${
        styles[status] || styles.draft
      }`}
    >
      {String(status || "draft").replaceAll("_", " ")}
    </span>
  );
};

const canAnswerGroup = (detail, group) => {
  if (detail?.current_user_system_role === "admin") return true;
  const role = String(detail?.current_user_project_role || "").toLowerCase();
  // QA/QC has Admin-equivalent authority inside Equipment and may complete
  // any checklist responsibility group (PE, Field, QA/QC, or 3rd Party).
  if (role === "qa_qc_manager") return true;
  if (!group || group === "any") return true;
  return ROLE_GROUPS[group]?.has(role) || false;
};

const fieldHasValue = (field, value, attachments = []) => {
  if (field.type === "attachment") {
    const matching = attachments.filter((item) => item.field_key === field.id);
    if (!field.required) return true;
    return matching.length > 0;
  }
  if (!field.required) return true;
  if (field.type === "checkbox") return value === true;
  return value !== null && value !== undefined && String(value).trim() !== "";
};

const FieldStatusDot = ({ complete }) => (
  <span
    className={`inline-block h-2 w-2 rounded-full ${
      complete ? "bg-emerald-500" : "bg-amber-400"
    }`}
  />
);

// Keep image names readable in Equipment documents, exports and downstream uploads.
const isImageAttachment = (file) =>
  file.type?.toLowerCase().startsWith("image/") ||
  /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?|svg)$/i.test(file.name || "");

const photoNamePart = (value, fallback) =>
  String(value || "").normalize("NFKC")
    .replace(/[^\p{L}\p{N}._-]+/gu, "-")
    .replace(/^[._-]+|[._-]+$/g, "").slice(0, 64) || fallback;

const makePhotoSuffix = () => {
  const date = new Date().toISOString().slice(0, 10).replaceAll("-", "");
  const random = typeof window.crypto?.getRandomValues === "function"
    ? window.crypto.getRandomValues(new Uint32Array(1))[0] % 1000000
    : Math.floor(Math.random() * 1000000);
  return `${date}_${String(random).padStart(6, "0")}`;
};

// Native file-input capture is useful on phones, but desktop/tablet Windows
// browsers do not reliably open the camera for `capture="environment"`.
// Preserve the existing native phone experience; use a real camera preview
// (getUserMedia) for Windows Surface and other desktop-class browsers.
const prefersNativePhoneCamera = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return Boolean(navigator.userAgentData?.mobile) ||
    /Android|iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

const cameraOpenError = (error) => {
  switch (error?.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Camera access was blocked. Allow the camera for this site in your browser and under Windows Settings → Privacy & security → Camera, then retry.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No camera was found. Check that the Surface camera is enabled and recognized by Windows.";
    case "NotReadableError":
    case "TrackStartError":
      return "The camera is unavailable or already in use. Close other camera applications and try again.";
    case "OverconstrainedError":
      return "This camera could not be opened. Try another camera or choose a photo instead.";
    default:
      return error?.message || "Could not open the camera. Check browser camera permissions and try again.";
  }
};

const checklistPhotoFilename = (file, equipmentName, title, suffix) => {
  const extension = file.name?.match(/\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?|svg)$/i)?.[1];
  const mimeExtension = {
    "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif",
    "image/webp": "webp", "image/heic": "heic", "image/heif": "heif",
    "image/avif": "avif", "image/bmp": "bmp", "image/tiff": "tiff",
    "image/svg+xml": "svg",
  }[file.type?.toLowerCase()];
  // Preserve the actual format; never relabel an unknown format as JPEG.
  const ext = extension || mimeExtension;
  return `${photoNamePart(equipmentName, "Equipment")}_${photoNamePart(title, "Photo")}_${suffix}${ext ? `.${ext.toLowerCase()}` : ""}`;
};

function ResponseField({
  field,
  value,
  disabled,
  row,
  projectId,
  instanceId,
  equipmentName,
  availableDocuments,
  onChange,
  onReload,
  onAttachmentUploaded,
  currentUserId,
  canManageAttachments,
}) {
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoTitle, setPhotoTitle] = useState("");
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraRequestIdRef = useRef(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraDevices, setCameraDevices] = useState([]);
  const [cameraDeviceId, setCameraDeviceId] = useState("");
  const photoFilename = pendingPhoto
    ? checklistPhotoFilename(pendingPhoto.file, equipmentName, photoTitle, pendingPhoto.suffix)
    : "";
  const [uploadProgress, setUploadProgress] = useState(0);
  const [linking, setLinking] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const attachments = (row.attachments || []).filter(
    (item) => item.field_key === field.id,
  );

  const legacySelectValue =
    ["select", "radio"].includes(field.type) &&
    value !== null &&
    value !== undefined &&
    value !== "" &&
    !(field.options || []).includes(String(value))
      ? String(value)
      : null;

  const linkExisting = async () => {
    if (!selectedDocumentId || disabled || linking) return;
    setLinking(true);
    setAttachmentError("");
    try {
      await equipmentAPI.linkDesignedChecklistAttachment(projectId, instanceId, {
        document_id: Number(selectedDocumentId),
        section_id: row.section_id,
        row_id: row.id,
        field_key: field.id,
      });
      setSelectedDocumentId("");
      await onReload?.();
    } catch (error) {
      setAttachmentError(apiMessage(error, "Failed to link document."));
    } finally {
      setLinking(false);
    }
  };

  const upload = async (file, filename = null) => {
    if (!file || disabled || uploading) return;
    setUploading(true);
    setUploadProgress(0);
    setAttachmentError("");
    try {
      const uploadFile = filename
        ? new File([file], filename, { type: file.type, lastModified: file.lastModified })
        : file;
      const response = await equipmentAPI.uploadDesignedChecklistAttachment(
        projectId,
        instanceId,
        uploadFile,
        {
          section_id: row.section_id,
          row_id: row.id,
          field_key: field.id,
        },
        (percent) => setUploadProgress(percent),
      );
      await onAttachmentUploaded?.(
        response.data?.checklist,
        response.data?.attachment,
      );
      setPendingPhoto(null);
      setPhotoTitle("");
    } catch (error) {
      setAttachmentError(apiMessage(error, "Failed to upload attachment."));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const stopCamera = useCallback(() => {
    // A pending permission request may resolve after Close. Ignore/stall-proof it.
    cameraRequestIdRef.current += 1;
    const stream = cameraStreamRef.current;
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.pause();
      cameraVideoRef.current.srcObject = null;
    }
    stream?.getTracks().forEach((track) => track.stop());
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setCameraOpen(false);
    setCameraDevices([]);
    setCameraDeviceId("");
    setCameraError("");
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    stopCamera();
    const requestId = cameraRequestIdRef.current;
    setCameraBusy(true);
    setCameraError("");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera access requires HTTPS and a compatible browser. Use Edge or Chrome over HTTPS, or choose a photo instead.");
      setCameraBusy(false);
      return;
    }

    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: cameraDeviceId
            ? { deviceId: { exact: cameraDeviceId } }
            : { facingMode: { ideal: "environment" } },
        });
      } catch (error) {
        // Some Windows camera drivers ignore facingMode constraints.
        if (cameraDeviceId || !["NotFoundError", "OverconstrainedError"].includes(error?.name)) {
          throw error;
        }
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
      }
      if (requestId !== cameraRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      cameraStreamRef.current = stream;
      const video = cameraVideoRef.current;
      if (!video) throw new Error("Camera preview is unavailable.");
      video.srcObject = stream;
      await video.play();
      if (requestId !== cameraRequestIdRef.current) return;

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (requestId === cameraRequestIdRef.current) {
          setCameraDevices(devices.filter((device) => device.kind === "videoinput" && device.deviceId));
        }
      } catch (_) {
        // The camera itself still works even if device enumeration is blocked.
      }
    } catch (error) {
      if (requestId !== cameraRequestIdRef.current) return;
      stopCamera();
      setCameraError(cameraOpenError(error));
      setCameraBusy(false);
    } finally {
      if (requestId === cameraRequestIdRef.current) setCameraBusy(false);
    }
  }, [cameraDeviceId, stopCamera]);

  useEffect(() => {
    if (!cameraOpen) return undefined;
    void startCamera();
    return () => stopCamera();
  }, [cameraOpen, startCamera, stopCamera]);

  const openCamera = () => {
    if (disabled || uploading || pendingPhoto) return;
    setAttachmentError("");
    if (prefersNativePhoneCamera()) {
      cameraInputRef.current?.click();
    } else {
      setCameraDeviceId("");
      setCameraOpen(true);
    }
  };

  const captureCameraPhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      setCameraError("The camera preview is not ready. Please retry.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Could not process the photo. Try another browser or choose a photo instead.");
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const requestId = cameraRequestIdRef.current;
    canvas.toBlob((blob) => {
      if (requestId !== cameraRequestIdRef.current) return;
      if (!blob) {
        setCameraError("Could not capture the photo. Please try again.");
        return;
      }
      const now = Date.now();
      const file = new File([blob], `camera-${now}.jpg`, { type: "image/jpeg", lastModified: now });
      setPhotoTitle(field.label || "Photo");
      setPendingPhoto({ file, suffix: makePhotoSuffix() });
      closeCamera();
    }, "image/jpeg", 0.9);
  };

  const selectAttachment = (event, camera = false) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // Allow selecting the same file again after cancel/retry.
    if (!file || disabled || uploading) return;
    setAttachmentError("");
    if (camera && !isImageAttachment(file)) {
      setAttachmentError("Please select an image for Take photo.");
      return;
    }
    if (isImageAttachment(file)) {
      setPhotoTitle(field.label || "Photo");
      setPendingPhoto({ file, suffix: makePhotoSuffix() });
    } else {
      upload(file);
    }
  };

  const remove = async (attachmentId) => {
    if (!window.confirm("Remove this checklist attachment?")) return;
    setAttachmentError("");
    try {
      await equipmentAPI.deleteDesignedChecklistAttachment(projectId, attachmentId);
      await onReload?.();
    } catch (error) {
      setAttachmentError(apiMessage(error, "Failed to remove attachment."));
    }
  };

  if (field.type === "attachment") {
    return (
      <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            Uploading or linking a document satisfies this field.
          </div>
          {!disabled && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={uploading || Boolean(pendingPhoto)}
                onClick={() => uploadInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" />
                {uploading ? `Uploading… ${uploadProgress}%` : "Upload new"}
              </button>
              <button
                type="button"
                disabled={uploading || Boolean(pendingPhoto)}
                onClick={openCamera}
                className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Camera className="h-3.5 w-3.5" /> Take photo
              </button>
              <input ref={uploadInputRef} type="file" className="hidden"
                disabled={uploading || Boolean(pendingPhoto)} onChange={selectAttachment} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                className="hidden" disabled={uploading || Boolean(pendingPhoto)}
                onChange={(event) => selectAttachment(event, true)} />
            </div>
          )}
        </div>

        {cameraOpen && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Take Equipment checklist photo"
            className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-950/80 p-3 sm:p-6"
          >
            <div className="flex w-full max-w-3xl flex-col gap-3 rounded-2xl bg-white p-4 shadow-2xl">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-base font-bold text-gray-950">Take checklist photo</div>
                  <div className="text-xs text-gray-500">Capture a photo using this device's camera.</div>
                </div>
                <button type="button" onClick={closeCamera}
                  className="rounded-lg border border-gray-300 p-2 text-gray-700" aria-label="Close camera">
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden rounded-xl bg-black sm:min-h-[360px]">
                <video ref={cameraVideoRef} autoPlay muted playsInline
                  className="max-h-[60vh] w-full object-contain" />
                {cameraBusy && (
                  <span className="absolute inset-0 flex items-center justify-center gap-2 bg-gray-950/60 text-sm font-semibold text-white">
                    <Loader2 className="h-5 w-5 animate-spin" /> Opening camera…
                  </span>
                )}
              </div>
              {cameraDevices.length > 1 && (
                <label className="flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-700">
                  Camera
                  <select value={cameraDeviceId} onChange={(event) => setCameraDeviceId(event.target.value)}
                    className="max-w-full flex-1 rounded-lg border border-gray-300 px-2.5 py-2 text-sm">
                    <option value="">Default (back camera preferred)</option>
                    {cameraDevices.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {cameraError && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {cameraError}
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => { closeCamera(); cameraInputRef.current?.click(); }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
                  Choose photo instead
                </button>
                {cameraError && !cameraBusy && (
                  <button type="button" onClick={() => void startCamera()}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
                    Retry camera
                  </button>
                )}
                <button type="button" disabled={cameraBusy || Boolean(cameraError) || !cameraStreamRef.current}
                  onClick={captureCameraPhoto}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  <Camera className="h-4 w-4" /> Capture photo
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

        {pendingPhoto && !disabled && (
          <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2.5">
            <label className="block text-xs font-semibold text-gray-700">
              Photo title / description
              <input
                type="text"
                value={photoTitle}
                maxLength={100}
                disabled={uploading}
                onChange={(event) => setPhotoTitle(event.target.value)}
                placeholder={field.label || "Describe this photo"}
                className={`${inputClass} mt-1 w-full`}
              />
            </label>
            <p className="break-all text-[11px] text-gray-600">
              Save as: <span className="font-semibold">{photoFilename}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={uploading || !photoTitle.trim()}
                onClick={() => upload(pendingPhoto.file, photoFilename)}
                className="rounded-lg bg-gray-950 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                {uploading ? "Uploading…" : "Upload photo"}
              </button>
              <button type="button" disabled={uploading}
                onClick={() => { setPendingPhoto(null); setPhotoTitle(""); setAttachmentError(""); }}
                className="rounded-lg border px-3 py-1.5 text-xs font-semibold text-gray-700 disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {uploading && (
          <div className="space-y-1.5" role="status" aria-live="polite">
            <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
              <span>Uploading document</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-gray-900 transition-[width] duration-150"
                style={{
                  width: `${Math.max(0, Math.min(100, Number(uploadProgress || 0)))}%`,
                }}
              />
            </div>
          </div>
        )}

        {!disabled && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={selectedDocumentId}
              onChange={(event) => setSelectedDocumentId(event.target.value)}
              className={`${inputClass} flex-1`}
            >
              <option value="">Link an existing Equipment document…</option>
              {(availableDocuments || []).map((document) => (
                <option key={document.document_id} value={document.document_id}>
                  {document.document_name}
                  {document.document_type ? ` · ${document.document_type}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selectedDocumentId || linking}
              onClick={linkExisting}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50"
            >
              <Link2 className="h-3.5 w-3.5" /> {linking ? "Linking…" : "Link"}
            </button>
          </div>
        )}

        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex flex-col gap-2 rounded-lg bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <a
                  href={attachment.file_url || "#"}
                  title={attachment.document_name}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-full items-start gap-1.5 text-xs font-semibold text-indigo-700 hover:underline"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-all">{attachment.document_name}</span>
                </a>
                <div className="mt-0.5 text-[11px] text-gray-500">
                  Uploaded {attachment.linked_at ? fmtTime(attachment.linked_at) : ""}
                </div>
              </div>
              <div className="flex shrink-0 gap-1.5">
                {(canManageAttachments || Number(attachment.linked_by) === Number(currentUserId)) && (
                  <button
                    type="button"
                    onClick={() => remove(attachment.id)}
                    className="rounded-md border px-2 py-1 text-[11px] font-bold text-gray-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          {!attachments.length && (
            <div className="rounded-lg border border-dashed px-3 py-3 text-center text-xs text-gray-400">
              No attachment linked yet.
            </div>
          )}
        </div>
        {attachmentError && (
          <div className="text-xs font-medium text-red-600">{attachmentError}</div>
        )}
      </div>
    );
  }

  if (field.type === "equipment_field") {
    return (
      <div className="rounded-md border bg-gray-50 px-2.5 py-1.5 text-sm font-semibold text-gray-800">
        {value ?? "—"}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <select
        disabled={disabled}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
        className={inputClass}
      >
        <option value="">Select…</option>
        {legacySelectValue && (
          <option value={legacySelectValue}>{legacySelectValue} (historical value)</option>
        )}
        {(field.options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "radio") {
    const choices = legacySelectValue
      ? [legacySelectValue, ...(field.options || [])]
      : field.options || [];
    return (
      <div className="flex flex-wrap gap-2">
        {choices.map((option) => (
          <label
            key={option}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm ${
              disabled ? "bg-gray-50 text-gray-500" : "bg-white"
            }`}
          >
            <input
              type="radio"
              name={`row-${row.id}-field-${field.id}`}
              disabled={disabled}
              checked={String(value ?? "") === String(option)}
              onChange={() => onChange(option)}
            />
            {option}
            {legacySelectValue === option && !(field.options || []).includes(option)
              ? " (historical)"
              : ""}
          </label>
        ))}
        {!disabled && value !== null && value !== undefined && value !== "" && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded-md border px-2 py-1 text-[11px] font-semibold text-gray-500"
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label
        className={`flex min-h-9 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm ${
          disabled ? "bg-gray-50 text-gray-500" : "bg-white"
        }`}
      >
        <input
          type="checkbox"
          disabled={disabled}
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        {field.placeholder || "Confirm"}
      </label>
    );
  }

  if (field.type === "textarea") {
    return (
      <textarea
        rows={2}
        disabled={disabled}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
        placeholder={field.placeholder || ""}
      />
    );
  }

  return (
    <input
      type={field.type === "number" ? "number" : "text"}
      disabled={disabled}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      className={inputClass}
      placeholder={field.placeholder || ""}
    />
  );
}

function StructuredChecklistExecution({
  projectId,
  instanceId,
  equipment,
  canFill,
  canAssign,
  canConfigure,
  canNotifyCompletion,
  currentUserId,
  onCreateTask,
  onChanged,
  onEditStateChange,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState({});
  const [baseline, setBaseline] = useState({});
  const [dirtyRows, setDirtyRows] = useState(new Set());
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [sendingCompletionEmail, setSendingCompletionEmail] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");
  const [expandedSectionIds, setExpandedSectionIds] = useState(new Set());
  const [selectedStageId, setSelectedStageId] = useState(null);
  const detailRef = useRef(null);
  const selectedStageRef = useRef(null);
  const draftsRef = useRef({});
  const baselineRef = useRef({});
  const dirtyRowsRef = useRef(new Set());
  const savingRef = useRef(false);
  const savePromiseRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const saveBatchRef = useRef(null);

  const hydrate = useCallback((next) => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const previousStage = detailRef.current?.current_lifecycle_stage;
    const nextStage = next?.current_lifecycle_stage;
    const availableStageIds = new Set((next?.stages || []).map((stage) => stage.id));
    const lifecycleAdvanced = Boolean(previousStage && nextStage && previousStage !== nextStage);
    const preferredStage =
      !lifecycleAdvanced && selectedStageRef.current && availableStageIds.has(selectedStageRef.current)
        ? selectedStageRef.current
        : nextStage;

    selectedStageRef.current = preferredStage || null;
    setSelectedStageId(preferredStage || null);

    const visibleSections = (next?.sections || [])
      .filter((section) => section.stage_id === preferredStage)
      .sort(
        (a, b) =>
          Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
          Number(a.id || 0) - Number(b.id || 0),
      );
    const visibleIds = new Set(visibleSections.map((section) => section.id));

    setExpandedSectionIds((current) => {
      const stillVisible = new Set([...current].filter((id) => visibleIds.has(id)));
      if (previousStage === nextStage && stillVisible.size > 0) return stillVisible;

      const firstIncomplete = visibleSections.find(
        (section) =>
          Number(section.progress?.required || 0) >
          Number(section.progress?.complete || 0),
      );
      const firstSection = firstIncomplete || visibleSections[0];
      return new Set(firstSection ? [firstSection.id] : []);
    });

    setDetail(next);
    detailRef.current = next;
    const values = {};
    (next?.sections || []).forEach((section) => {
      (section.rows || []).forEach((row) => {
        values[row.id] = {
          fields: { ...(row.response?.fields || {}) },
        };
      });
    });
    draftsRef.current = values;
    baselineRef.current = values;
    dirtyRowsRef.current = new Set();
    setDrafts(values);
    setBaseline(values);
    setDirtyRows(new Set());
    setSaveStatus("idle");
    setLastSavedAt(null);
  }, []);

  const load = useCallback(
    async () => {
      if (!projectId || !instanceId) return;
      setLoading(true);
      setError("");
      try {
        const [checklistResponse, documentsResponse] = await Promise.all([
          equipmentAPI.getDesignedChecklist(projectId, instanceId),
          equipment?.id
            ? equipmentAPI.listDocuments(projectId, equipment.id)
            : Promise.resolve({ data: [] }),
        ]);
        hydrate(checklistResponse.data);
        setAvailableDocuments(documentsResponse.data || []);
      } catch (err) {
        setError(apiMessage(err, "Failed to load Equipment checklist."));
      } finally {
        setLoading(false);
      }
    },
    [projectId, instanceId, equipment?.id, hydrate],
  );

  useEffect(() => {
    load();
  }, [load]);

  const applyAttachmentUpload = useCallback((serverDetail, attachment) => {
    if (!serverDetail || !detailRef.current) return;
    const previousStage = detailRef.current.current_lifecycle_stage;
    const nextDetail = { ...detailRef.current, ...serverDetail };
    detailRef.current = nextDetail;
    setDetail(nextDetail);

    if (attachment?.document_id) {
      setAvailableDocuments((current) => {
        if (current.some((item) => Number(item.document_id) === Number(attachment.document_id))) {
          return current;
        }
        return [
          {
            document_id: attachment.document_id,
            document_name: attachment.document_name,
            document_type: attachment.document_type || "Equipment Checklist Attachment",
          },
          ...current,
        ];
      });
    }

    const nextStage = serverDetail.current_lifecycle_stage;
    if (nextStage && nextStage !== previousStage) {
      selectedStageRef.current = nextStage;
      setSelectedStageId(nextStage);
      const visibleSections = (serverDetail.sections || [])
        .filter((section) => section.stage_id === nextStage)
        .sort(
          (a, b) =>
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            Number(a.id || 0) - Number(b.id || 0),
        );
      const firstIncomplete = visibleSections.find(
        (section) =>
          Number(section.progress?.required || 0) >
          Number(section.progress?.complete || 0),
      );
      const firstSection = firstIncomplete || visibleSections[0];
      setExpandedSectionIds(new Set(firstSection ? [firstSection.id] : []));
    }
  }, []);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      const runner = saveBatchRef.current;
      if (runner) void runner();
    }, AUTOSAVE_DELAY_MS);
  }, []);

  const updateField = useCallback((rowId, fieldId, value) => {
    // Update refs synchronously so autosave never depends on React state batching.
    const currentDrafts = draftsRef.current || {};
    // Do not create a new dirty row (or reset the debounce timer) when a
    // controlled input reports the value that is already in the draft.
    if (Object.is(currentDrafts[rowId]?.fields?.[fieldId], value)) return;
    const nextDrafts = {
      ...currentDrafts,
      [rowId]: {
        fields: {
          ...(currentDrafts[rowId]?.fields || {}),
          [fieldId]: value,
        },
      },
    };
    const nextDirty = new Set(dirtyRowsRef.current);
    if (sameDraftValue(nextDrafts[rowId], baselineRef.current[rowId])) {
      nextDirty.delete(rowId);
    } else {
      nextDirty.add(rowId);
    }

    draftsRef.current = nextDrafts;
    dirtyRowsRef.current = nextDirty;
    setDrafts(nextDrafts);
    setDirtyRows(new Set(nextDirty));
    setSaveStatus(nextDirty.size ? "pending" : "idle");
    if (nextDirty.size) {
      scheduleAutosave();
    } else if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, [scheduleAutosave]);

  const saveBatch = useCallback(async () => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (!detailRef.current) return true;
    if (savingRef.current) return savePromiseRef.current || true;

    const rowIds = [...dirtyRowsRef.current];
    if (!rowIds.length) return true;

    const rowValues = {};
    const baselineValues = {};
    rowIds.forEach((rowId) => {
      rowValues[String(rowId)] = draftsRef.current[rowId] || { fields: {} };
      baselineValues[String(rowId)] = baselineRef.current[rowId] || { fields: {} };
    });

    setSaving(true);
    savingRef.current = true;
    setSaveStatus("saving");
    setError("");

    const request = (async () => {
      try {
        const response = await equipmentAPI.saveDesignedChecklistResponses(
          projectId,
          instanceId,
          {
            expected_updated_at: detailRef.current?.updated_at,
            baseline_header_values: {},
            baseline_row_values: baselineValues,
            header_values: {},
            row_values: rowValues,
          },
        );

        const serverDetail = response.data || {};
        const nextBaseline = { ...baselineRef.current };
        const nextDirty = new Set(dirtyRowsRef.current);
        const nextDrafts = { ...draftsRef.current };
        let reconciledDrafts = false;

        rowIds.forEach((rowId) => {
          const key = String(rowId);
          const savedDraft = savedRowDraft(serverDetail, rowId, rowValues[key]);
          nextBaseline[rowId] = savedDraft;
          // Browser input values (e.g. "12") may be normalized by the API
          // (e.g. 12). When nothing changed since the request was sent,
          // acknowledge the server value rather than endlessly resaving it.
          // Preserve any genuinely newer edits made while saving.
          if (
            sameDraftValue(draftsRef.current[rowId], rowValues[key]) ||
            sameDraftValue(draftsRef.current[rowId], savedDraft)
          ) {
            nextDrafts[rowId] = savedDraft;
            reconciledDrafts = true;
            nextDirty.delete(rowId);
          } else {
            nextDirty.add(rowId);
          }
        });

        baselineRef.current = nextBaseline;
        dirtyRowsRef.current = nextDirty;
        if (reconciledDrafts) {
          draftsRef.current = nextDrafts;
          setDrafts(nextDrafts);
        }
        setBaseline(nextBaseline);
        setDirtyRows(new Set(nextDirty));

        const merged = mergeAutosaveDetail(
          detailRef.current,
          serverDetail,
          new Set(rowIds),
        );
        detailRef.current = merged;
        setDetail(merged);
        setLastSavedAt(new Date());
        setSaveStatus(nextDirty.size ? "pending" : "saved");
        // If the user edited again while this request was in flight, queue the
        // remaining dirty values instead of relying on a React render/effect.
        if (nextDirty.size) scheduleAutosave();
        return true;
      } catch (err) {
        setError(apiMessage(err, "Failed to autosave checklist responses."));
        setSaveStatus("error");
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    })();

    savePromiseRef.current = request;
    const result = await request;
    if (savePromiseRef.current === request) savePromiseRef.current = null;
    return result;
  }, [instanceId, projectId, scheduleAutosave]);

  // The field-change timer always calls the latest save implementation.
  saveBatchRef.current = saveBatch;

  const save = useCallback(async () => {
    // Flush all pending edits. This is used before switching checklist/tab or
    // closing the drawer. Normal editing uses the debounced saveBatch below.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (savingRef.current && savePromiseRef.current) {
        const activeResult = await savePromiseRef.current;
        if (activeResult === false) return false;
      }
      if (!dirtyRowsRef.current.size) return true;
      const okay = await saveBatch();
      if (okay === false) return false;
    }
    return dirtyRowsRef.current.size === 0;
  }, [saveBatch]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const discard = useCallback(() => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const restored = baselineRef.current;
    draftsRef.current = restored;
    dirtyRowsRef.current = new Set();
    setDrafts(restored);
    setBaseline(restored);
    setDirtyRows(new Set());
    setSaveStatus("idle");
  }, []);

  useEffect(() => {
    onEditStateChange?.({
      dirty: dirtyRows.size > 0,
      saving,
      save,
      discard,
      lastSavedAt,
    });
  }, [dirtyRows, saving, save, discard, lastSavedAt, onEditStateChange]);

  const sendCompletionEmail = async () => {
    if (!equipment?.id || sendingCompletionEmail) return;
    const confirmed = window.confirm(
      "Send the 100% equipment completion email to all active Operations Managers and Supervisors on this project?",
    );
    if (!confirmed) return;
    setSendingCompletionEmail(true);
    try {
      await equipmentAPI.sendCompletionNotification(projectId, equipment.id);
      await onChanged?.();
    } catch (err) {
      window.alert(apiMessage(err, "Failed to send the equipment completion email."));
    } finally {
      setSendingCompletionEmail(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className="flex justify-center rounded-2xl border bg-white p-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }
  if (error && !detail) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!detail) return null;

  const stages = detail.stages || [];
  const currentStageIndex = Math.max(
    0,
    stages.findIndex((stage) => stage.id === detail.current_lifecycle_stage),
  );
  const currentStageData = stages[currentStageIndex] || {};
  const resolvedSelectedStageId = stages.some((stage) => stage.id === selectedStageId)
    ? selectedStageId
    : detail.current_lifecycle_stage;
  const selectedStageIndex = Math.max(
    0,
    stages.findIndex((stage) => stage.id === resolvedSelectedStageId),
  );
  const selectedStageData = stages[selectedStageIndex] || currentStageData;
  const locked = detail.status === "archived";
  const sections = (detail.sections || []).filter(
    (section) => section.stage_id === resolvedSelectedStageId,
  ).sort(
    (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0) || Number(a.id || 0) - Number(b.id || 0),
  );
  const selectedProgress = detail.stage_progress?.[selectedStageData.id] || {
    percent: 100,
    required: 0,
    complete: 0,
    pending: 0,
    failed: 0,
  };
  const showLegend = detail.definition?.report_layout?.show_color_legend !== false;
  const legend = detail.color_legend || [];
  const canManageAttachments =
    detail?.current_user_system_role === "admin" ||
    String(detail?.current_user_project_role || "").toLowerCase() === "qa_qc_manager";

  const equipmentReadiness = Number(equipment?.readiness?.completion_percent ?? 0);
  const completionNotification = equipment?.completion_notification || {};
  const completionEmailSent = Boolean(completionNotification.sent);
  const completionEmailAvailable = Boolean(
    canNotifyCompletion &&
      equipmentReadiness === 100 &&
      equipment?.status === "energized_commissioned",
  );
  const overallCalculated = Number(detail.completion_required || 0) > 0;
  const stageHasRequirements = Number(selectedProgress.required || 0) > 0;

  const selectStage = (stageId) => {
    if (!stageId || stageId === resolvedSelectedStageId) return;
    selectedStageRef.current = stageId;
    setSelectedStageId(stageId);

    const stageSections = (detail.sections || [])
      .filter((section) => section.stage_id === stageId)
      .sort(
        (a, b) =>
          Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
          Number(a.id || 0) - Number(b.id || 0),
      );
    const firstIncomplete = stageSections.find(
      (section) =>
        Number(section.progress?.required || 0) >
        Number(section.progress?.complete || 0),
    );
    const firstSection = firstIncomplete || stageSections[0];
    setExpandedSectionIds(new Set(firstSection ? [firstSection.id] : []));
  };

  const toggleSection = (sectionId) => {
    setExpandedSectionIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const downloadPdf = async (mode) => {
    if (!instanceId || pdfBusy) return;
    setPdfBusy(mode);
    setError("");
    try {
      // Filled reports must represent persisted execution data, never a stale
      // server snapshot while the browser still has debounced edits pending.
      if (mode === "filled") {
        const saved = await save();
        if (saved === false) return;
      }

      const response = await equipmentAPI.downloadDesignedChecklistPdf(
        projectId,
        instanceId,
        mode,
      );
      const disposition = response?.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackBase = String(detail.equipment_tag || detail.equipment_number || "Equipment")
        .replace(/[^A-Za-z0-9._-]+/g, "_");
      const suffix = mode === "blank" ? "BLANK" : "FILLED";
      const fallbackName = `${fallbackBase}_${suffix}.pdf`;
      const filename = filenameMatch?.[1] || fallbackName;
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(
        apiMessage(
          err,
          `Failed to generate ${mode} checklist PDF.`,
        ),
      );
    } finally {
      setPdfBusy("");
    }
  };

  return (
    <div className="space-y-2">
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="px-3.5 py-3 sm:px-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <ClipboardCheck className="h-4 w-4 shrink-0 text-orange-500" />
                <h3 className="truncate text-base font-black text-gray-950">{detail.name}</h3>
                <StatusPill status={detail.status} />
                <span
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold"
                  style={{
                    backgroundColor: tint(currentStageData.color, 0.18),
                    borderColor: tint(currentStageData.color, 0.5),
                    color: safeColor(currentStageData.color, "#C2410C"),
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: safeColor(currentStageData.color, "#F97316") }}
                  />
                  {currentStageData.label || "Current stage"}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {detail.equipment_number} · Template v{detail.template_version_number} · Revision {detail.template_content_revision}
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-2 sm:min-w-[320px] sm:flex-row sm:items-center sm:justify-end">
              <div className="min-w-0 flex-1 sm:max-w-[300px]">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Overall checklist progress
                    </div>
                    <div className="text-xl font-black text-orange-600">
                      {overallCalculated ? `${detail.overall_completion_percent}%` : "—"}
                    </div>
                  </div>
                  {!overallCalculated && (
                    <span className="pb-1 text-[11px] font-semibold text-gray-400">Not calculated</span>
                  )}
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-all"
                    style={{ width: `${overallCalculated ? detail.overall_completion_percent : 0}%` }}
                  />
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 text-xs font-semibold">
                {saving ? (
                  <span className="inline-flex items-center gap-1.5 text-orange-600">
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </span>
                ) : dirtyRows.size > 0 ? (
                  <button
                    type="button"
                    onClick={save}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-orange-700 hover:bg-orange-100"
                    title="Autosave is pending; save immediately"
                  >
                    <Save className="h-4 w-4" /> Autosave pending · Save now
                  </button>
                ) : saveStatus === "saved" && lastSavedAt ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700">
                    <Check className="h-4 w-4" /> Saved {lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </span>
                ) : saveStatus === "error" ? (
                  <span className="text-red-600">Autosave failed</span>
                ) : null}
                {canConfigure && (
                  <button
                    type="button"
                    onClick={() => downloadPdf("blank")}
                    disabled={Boolean(pdfBusy)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    title="Download the checklist structure with empty execution responses"
                  >
                    {pdfBusy === "blank" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Blank PDF
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => downloadPdf("filled")}
                  disabled={Boolean(pdfBusy) || saving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gray-950 px-3 py-2 text-white hover:bg-gray-800 disabled:opacity-40"
                  title="Save pending responses and download the filled checklist"
                >
                  {pdfBusy === "filled" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Filled PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-3 py-2 sm:px-4">
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-[820px] items-start">
              {stages.map((stage, index) => {
                const progress = detail.stage_progress?.[stage.id] || { percent: 100, required: 0 };
                const isCurrent = stage.id === detail.current_lifecycle_stage;
                const isSelected = stage.id === resolvedSelectedStageId;
                const isPast = index < currentStageIndex;
                const isFuture = index > currentStageIndex;
                const hasRequirements = Number(progress.required || 0) > 0;
                return (
                  <React.Fragment key={stage.id}>
                    <button
                      type="button"
                      onClick={() => selectStage(stage.id)}
                      className={`min-w-0 flex-1 rounded-lg px-1.5 py-1 text-left transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-100 ${
                        isSelected ? "bg-gray-50" : ""
                      }`}
                      aria-pressed={isSelected}
                      title={isCurrent ? "Current lifecycle stage" : `Open ${stage.label}`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 ${
                            isSelected ? "shadow-sm ring-2 ring-orange-50" : "bg-white"
                          }`}
                          style={{
                            borderColor: isSelected || isCurrent || isPast
                              ? safeColor(stage.color, "#F97316")
                              : tint(stage.color, 0.55),
                            backgroundColor: isSelected ? tint(stage.color, 0.14) : "#FFFFFF",
                          }}
                        >
                          {isPast ? (
                            <Check className="h-3.5 w-3.5" style={{ color: safeColor(stage.color, "#059669") }} />
                          ) : (
                            <ClipboardCheck
                              className="h-3.5 w-3.5"
                              style={{ color: safeColor(stage.color, isFuture ? "#6B7280" : "#F97316") }}
                            />
                          )}
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <div className={`text-[11px] font-black leading-4 ${isSelected ? "text-gray-950" : "text-gray-700"}`}>
                            {stage.label}
                          </div>
                          <div className={`mt-0.5 text-[9px] font-bold ${isCurrent ? "text-orange-600" : isSelected ? "text-gray-600" : "text-gray-400"}`}>
                            {isCurrent
                              ? hasRequirements
                                ? `Current stage · ${progress.percent}%`
                                : "Current stage"
                              : isPast
                                ? "Completed stage"
                                : hasRequirements
                                  ? `${progress.percent}% complete`
                                  : "No required items"}
                          </div>
                        </div>
                      </div>
                    </button>
                    {index < stages.length - 1 && (
                      <div className="mx-2 mt-[14px] h-px w-7 shrink-0 border-t border-dashed border-gray-300" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm sm:px-4">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {showLegend && legend.length > 0 && (
              <>
                <div className="mr-0.5 flex shrink-0 items-center gap-1 text-[10px] font-black uppercase tracking-wide text-gray-400">
                  <ShieldCheck className="h-3 w-3" /> Form color legend
                </div>
                {legend.map((item) => (
                  <span
                    key={item.id}
                    className="rounded-md border px-2 py-0.5 text-[10px] text-gray-700"
                    style={{ backgroundColor: tint(item.color, 0.22), borderColor: tint(item.color, 0.55) }}
                    title={item.description || item.label}
                  >
                    <strong>{item.label}</strong>
                    {item.description ? <span className="ml-1 text-gray-500">· {item.description}</span> : null}
                  </span>
                ))}
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2 text-[11px]">
            <span className="font-bold text-gray-700">{selectedStageData.label} checklist</span>
            {stageHasRequirements ? (
              <span className="font-semibold text-gray-500">
                <strong className="text-orange-600">{selectedProgress.complete} of {selectedProgress.required}</strong> required complete · {selectedProgress.percent}%
              </span>
            ) : (
              <span className="font-semibold text-gray-400">No lifecycle completion items</span>
            )}
          </div>
        </div>
        {stageHasRequirements && (
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${selectedProgress.percent}%`, backgroundColor: safeColor(selectedStageData.color, "#F97316") }}
            />
          </div>
        )}
      </section>

      <div className="space-y-2">
        {sections.map((section) => {
          const isExpanded = expandedSectionIds.has(section.id);
          const requiredCount = Number(section.progress?.required || 0);
          const completeCount = Number(section.progress?.complete || 0);
          const sectionPercent = Number(section.progress?.percent || 0);
          const sectionComplete = requiredCount > 0 && completeCount >= requiredCount;
          const rowCount = Number(section.rows?.length || 0);
          const summary = section.description || `${rowCount} checklist item${rowCount === 1 ? "" : "s"}`;
          const sectionColor = safeColor(section.legend?.color || section.color || selectedStageData.color || "#E5E7EB");

          return (
            <section
              key={section.id}
              className="overflow-hidden rounded-xl border shadow-sm transition-shadow hover:shadow-md"
              style={layeredToneStyle(sectionColor, { from: isExpanded ? 0.24 : 0.2, to: 0.08, borderAlpha: 0.36 })}
            >
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left sm:px-3.5"
                style={{ backgroundColor: tint(sectionColor, isExpanded ? 0.14 : 0.09) }}
              >
                <div
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-gray-600"
                  style={{ backgroundColor: tint(sectionColor, 0.22), border: `1px solid ${tint(sectionColor, 0.42)}` }}
                >
                  {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-black text-gray-950">{section.title}</h4>
                    {section.section_kind === "equipment_info" && (
                      <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-black uppercase text-orange-700">
                        {section.counts_toward_lifecycle ? "Other · lifecycle gate" : "Other · informational"}
                      </span>
                    )}
                    {section.legend?.label && (
                      <span
                        className="rounded-full border px-2 py-0.5 text-[10px] font-bold text-gray-700"
                        style={{
                          backgroundColor: tint(section.legend.color, 0.22),
                          borderColor: tint(section.legend.color, 0.55),
                        }}
                      >
                        {section.legend.label}
                      </span>
                    )}
                  </div>
                  <p className="line-clamp-1 text-[11px] text-gray-500">{summary}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {section.counts_toward_lifecycle ? (
                    <>
                      <span className={`text-[11px] font-black ${sectionComplete ? "text-emerald-600" : "text-gray-500"}`}>
                        {requiredCount > 0 ? `${completeCount}/${requiredCount}` : "—"}
                      </span>
                      <span
                        className={`grid h-4 w-4 place-items-center rounded-full border-2 ${
                          sectionComplete ? "border-emerald-500 text-emerald-600" : "border-gray-300 text-gray-300"
                        }`}
                        title={requiredCount > 0 ? `${sectionPercent}% complete` : "No required items"}
                      >
                        {sectionComplete ? <Check className="h-3 w-3" /> : null}
                      </span>
                    </>
                  ) : (
                    <span className="rounded-full bg-gray-50 px-2 py-1 text-[10px] font-bold text-gray-400">Not calculated</span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div
                  className="border-t p-1.5 sm:p-2"
                  style={{ borderColor: tint(sectionColor, 0.26), backgroundColor: tint(sectionColor, 0.08) }}
                >
                  <div className="space-y-1.5">
                    {(section.rows || []).map((row, rowIndex) => {
                      const draft = drafts[row.id] || { fields: {} };
                      const groupAllowed = canAnswerGroup(detail, row.responsibility_group);
                      const editable = Boolean(
                        groupAllowed && canFill && !locked,
                      );
                      const rowColor = safeColor(row.legend?.color || row.color || section.legend?.color || section.color);
                      const rowComplete = row.status === "complete";

                      return (
                        <article
                          key={row.id}
                          className="rounded-lg border px-2.5 py-2 shadow-sm"
                          style={{
                            ...toneStyle(rowColor, { bgAlpha: 0.1, borderAlpha: 0.28 }),
                            borderLeftWidth: 4,
                            borderLeftColor: rowColor,
                          }}
                        >
                          <div className="grid gap-2 xl:grid-cols-[minmax(200px,.72fr)_minmax(0,1.28fr)_auto] xl:items-center">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span
                                  className="rounded-md border px-1.5 py-0.5 text-[9px] font-black"
                                  style={{ backgroundColor: tint(rowColor, 0.18), borderColor: tint(rowColor, 0.5) }}
                                >
                                  {row.legend?.label || ROLE_LABELS[row.responsibility_group] || row.responsibility_group || `Item ${rowIndex + 1}`}
                                </span>
                                <h5 className="min-w-0 text-sm font-bold leading-4 text-gray-950">{row.label}</h5>
                                {row.is_required && section.counts_toward_lifecycle && (
                                  <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-[9px] font-bold text-rose-600">Required</span>
                                )}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-gray-400">
                                {row.help_text && <span className="line-clamp-1 max-w-full">{row.help_text}</span>}
                                {row.answered_at && (
                                  <>
                                    <span className="inline-flex items-center gap-1">
                                      <UserRound className="h-3 w-3" /> {row.answered_by_name || "User"}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Clock3 className="h-3 w-3" /> {fmtTime(row.answered_at)}
                                    </span>
                                  </>
                                )}
                                {!groupAllowed && <span className="font-semibold text-gray-500">View only for your role</span>}
                              </div>
                            </div>

                            <div className={`grid gap-1.5 ${Number(row.fields?.length || 0) > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}>
                              {(row.fields || []).map((field) => {
                                const fieldValue = draft.fields?.[field.id];
                                const complete = fieldHasValue(field, fieldValue, row.attachments || []);
                                return (
                                  <div key={field.id} className="min-w-0">
                                    <div className="mb-0.5 flex items-center justify-between gap-1.5">
                                      <label className="flex min-w-0 items-center gap-1 text-[9px] font-black uppercase tracking-wide text-gray-400">
                                        {field.type === "attachment" ? (
                                          <Paperclip className="h-3 w-3 shrink-0" />
                                        ) : (
                                          <FieldStatusDot complete={complete} />
                                        )}
                                        <span className="truncate">{field.label}</span>
                                        {field.required ? <span className="text-red-500">*</span> : null}
                                      </label>
                                      <span className="shrink-0 text-[8px] font-semibold uppercase text-gray-300">
                                        {field.type.replaceAll("_", " ")}
                                      </span>
                                    </div>
                                    <ResponseField
                                      field={field}
                                      value={fieldValue}
                                      disabled={!editable}
                                      row={{ ...row, section_id: section.id }}
                                      projectId={projectId}
                                      instanceId={instanceId}
                                      equipmentName={equipment?.equipment_tag || equipment?.equipment_number || equipment?.type || `Equipment-${equipment?.id}`}
                                      availableDocuments={availableDocuments}
                                      onChange={(value) => updateField(row.id, field.id, value)}
                                      onReload={() => load()}
                                      onAttachmentUploaded={applyAttachmentUpload}
                                      currentUserId={currentUserId}
                                      canManageAttachments={canManageAttachments}
                                    />
                                    {field.help_text && <p className="mt-0.5 text-[9px] text-gray-400">{field.help_text}</p>}
                                  </div>
                                );
                              })}
                              {!row.fields?.length && (
                                <div className="rounded-lg border border-dashed px-3 py-2 text-center text-xs text-gray-400">
                                  No response fields configured.
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-1.5 xl:justify-end">
                              <span
                                className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold ${
                                  rowComplete ? "text-emerald-600" : row.status === "failed" ? "text-rose-600" : "text-gray-400"
                                }`}
                              >
                                {rowComplete ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : row.status === "failed" ? (
                                  <XCircle className="h-4 w-4" />
                                ) : (
                                  <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300" />
                                )}
                                {rowComplete ? "Complete" : String(row.status || "pending").replaceAll("_", " ")}
                              </span>

                              {canAssign && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onCreateTask?.({
                                      title: `Checklist: ${row.label}`,
                                      question: row.label,
                                      instructions: row.help_text || "Complete or clarify this Equipment checklist item.",
                                      lifecycle_stage: section.lifecycle_stage,
                                      checklist_instance_id: detail.id,
                                      checklist_instance_row_id: row.id,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-[10px] font-bold text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                  title="Assign this checklist item as an Equipment task"
                                >
                                  <UserRound className="h-3.5 w-3.5" /> Assign
                                </button>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}

                    {!section.rows?.length && (
                      <div className="rounded-lg border border-dashed bg-white p-6 text-center text-sm text-gray-500">
                        No items are configured for this block.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          );
        })}

        {!sections.length && (
          <div className="rounded-2xl border border-dashed bg-white p-10 text-center text-sm text-gray-500">
            No Parts or Other blocks are configured for this lifecycle stage.
          </div>
        )}
      </div>

      {completionEmailAvailable && !completionEmailSent && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 font-bold text-emerald-900">
                <MailCheck className="h-5 w-5" /> Equipment is 100% complete
              </div>
              <p className="mt-1 text-xs text-emerald-700">
                Confirm to notify active Operations Managers and Supervisors.
              </p>
            </div>
            <button
              type="button"
              disabled={sendingCompletionEmail}
              onClick={sendCompletionEmail}
              className="rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {sendingCompletionEmail ? "Sending…" : "Confirm & send completion email"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default function DesignedChecklistPanel({
  projectId,
  equipment,
  canFill,
  canAssign,
  canConfigure,
  canNotifyCompletion,
  currentUserId,
  onCreateTask,
  onChanged,
  onEditStateChange,
}) {
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [templateVersionId, setTemplateVersionId] = useState("");
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingInstanceId, setPendingInstanceId] = useState(null);
  const editActionsRef = useRef({ save: null, discard: null, dirty: false });

  const reportEditState = useCallback(
    (next) => {
      editActionsRef.current = next || { save: null, discard: null, dirty: false };
      onEditStateChange?.(next);
    },
    [onEditStateChange],
  );

  const load = useCallback(async () => {
    if (!projectId || !equipment?.id) return;
    setLoading(true);
    setError("");
    try {
      const requests = [
        equipmentAPI.listDesignedChecklists(projectId, equipment.id),
      ];
      if (canConfigure) {
        requests.push(
          equipmentAPI.listChecklistTemplates(projectId, {
            classification_id: equipment.classification_id || equipment.equipment_type_id,
            type_id: equipment.type_id || undefined,
            published_only: true,
          }),
        );
      }
      const responses = await Promise.all(requests);
      const nextInstances = responses[0].data || [];
      setInstances(nextInstances);
      setTemplates(canConfigure ? responses[1]?.data || [] : []);
      setSelectedInstanceId((current) =>
        current && nextInstances.some((item) => item.id === current)
          ? current
          : nextInstances[0]?.id || null,
      );
    } catch (err) {
      setError(apiMessage(err, "Failed to load Equipment checklists."));
    } finally {
      setLoading(false);
    }
  }, [projectId, equipment?.id, equipment?.classification_id, equipment?.equipment_type_id, equipment?.type_id, canConfigure]);

  useEffect(() => {
    load();
  }, [load]);

  const publishedOptions = useMemo(
    () =>
      templates.flatMap((template) =>
        template.published_version
          ? [{ ...template.published_version, template_name: template.name }]
          : [],
      ),
    [templates],
  );

  const assign = async () => {
    if (!templateVersionId) return;
    setLoading(true);
    setError("");
    try {
      const response = await equipmentAPI.assignDesignedChecklist(
        projectId,
        equipment.id,
        { template_version_id: Number(templateVersionId) },
      );
      setTemplateVersionId("");
      await load();
      setSelectedInstanceId(response.data.id);
      await onChanged?.(response.data);
    } catch (err) {
      setError(apiMessage(err, "Failed to assign Equipment checklist."));
    } finally {
      setLoading(false);
    }
  };

  const unlink = async () => {
    if (!selectedInstanceId || loading) return;
    const selected = instances.find((item) => item.id === selectedInstanceId);
    const dirtyWarning = editActionsRef.current?.dirty
      ? " Unsaved checklist edits will be discarded."
      : "";
    const confirmed = window.confirm(
      `Unlink ${selected?.name || "this checklist"} from this equipment?${dirtyWarning} Saved responses and audit history will be preserved, but this checklist will no longer affect readiness.`,
    );
    if (!confirmed) return;

    setLoading(true);
    setError("");
    try {
      await equipmentAPI.unlinkDesignedChecklist(
        projectId,
        equipment.id,
        selectedInstanceId,
      );
      editActionsRef.current?.discard?.();
      setSelectedInstanceId(null);
      await load();
      await onChanged?.({
        equipment_id: equipment.id,
        instance_id: selectedInstanceId,
        unlinked: true,
      });
    } catch (err) {
      setError(apiMessage(err, "Failed to unlink Equipment checklist."));
    } finally {
      setLoading(false);
    }
  };

  const chooseInstance = (nextId) => {
    if (Number(nextId) === selectedInstanceId) return;
    if (editActionsRef.current?.dirty) {
      setPendingInstanceId(Number(nextId));
      return;
    }
    setSelectedInstanceId(Number(nextId));
  };

  const saveAndSwitch = async () => {
    const okay = await editActionsRef.current?.save?.();
    if (okay === false) return;
    setSelectedInstanceId(pendingInstanceId);
    setPendingInstanceId(null);
  };

  const discardAndSwitch = () => {
    editActionsRef.current?.discard?.();
    setSelectedInstanceId(pendingInstanceId);
    setPendingInstanceId(null);
  };

  return (
    <div className="space-y-4">
      {canConfigure && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div>
            <h3 className="font-bold text-gray-950">Checklist administration</h3>
            <p className="mt-1 text-sm text-gray-500">
              Manage the checklist assigned to this equipment. Unlink the current checklist to clear it, then assign another published template from the same control.
            </p>
          </div>
          <div className="mt-4">
            {!!instances.length ? (
              <div className="flex items-end gap-2">
                <label className="flex-1 text-xs font-semibold text-gray-600">
                  Assigned checklist
                  <select
                    value={selectedInstanceId || ""}
                    onChange={(event) => chooseInstance(event.target.value)}
                    className={`${inputClass} mt-1`}
                  >
                    {instances.map((instance) => (
                      <option key={instance.id} value={instance.id}>
                        {instance.name} · Template v{instance.template_version_number}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={unlink}
                  disabled={!selectedInstanceId || loading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-40"
                  title="Unlink this checklist from the equipment while preserving its history"
                >
                  <XCircle className="h-4 w-4" /> Unlink
                </button>
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <label className="flex-1 text-xs font-semibold text-gray-600">
                  Assigned checklist
                  <select
                    value={templateVersionId}
                    onChange={(event) => setTemplateVersionId(event.target.value)}
                    className={`${inputClass} mt-1`}
                  >
                    <option value="">No checklist assigned — select a published template</option>
                    {publishedOptions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.template_name} · v{version.version_number}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={assign}
                  disabled={!templateVersionId || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" /> Assign
                </button>
              </div>
            )}
          </div>
          {error && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      )}

      {!canConfigure && instances.length > 1 && (
        <label className="block rounded-xl border bg-white p-3 text-xs font-semibold text-gray-600">
          Checklist
          <select
            value={selectedInstanceId || ""}
            onChange={(event) => chooseInstance(event.target.value)}
            className={`${inputClass} mt-1`}
          >
            {instances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.name} · v{instance.template_version_number}
              </option>
            ))}
          </select>
        </label>
      )}

      {selectedInstanceId ? (
        <StructuredChecklistExecution
          projectId={projectId}
          instanceId={selectedInstanceId}
          equipment={equipment}
          canFill={canFill}
          canAssign={canAssign}
          canConfigure={canConfigure}
          canNotifyCompletion={canNotifyCompletion}
          currentUserId={currentUserId}
          onCreateTask={onCreateTask}
          onChanged={onChanged}
          onEditStateChange={reportEditState}
        />
      ) : !loading ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <ClipboardCheck className="mx-auto h-10 w-10 text-gray-300" />
          <h4 className="mt-3 font-bold text-gray-900">No Equipment checklist assigned</h4>
          <p className="mt-1 text-sm text-gray-500">
            {canConfigure
              ? "Select a published template from Checklist administration above."
              : "QA/QC must publish and assign a flexible checklist template first."}
          </p>
        </div>
      ) : (
        <div className="flex justify-center p-12">
          <Loader2 className="h-7 w-7 animate-spin text-orange-500" />
        </div>
      )}

      {pendingInstanceId && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-gray-950/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-950">Unsaved checklist changes</h3>
            <p className="mt-2 text-sm text-gray-600">
              Save or discard the current responses before opening another checklist.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingInstanceId(null)}
                className="rounded-xl border px-4 py-2.5 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={discardAndSwitch}
                className="rounded-xl border border-red-300 px-4 py-2.5 text-sm font-semibold text-red-700"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={saveAndSwitch}
                className="rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white"
              >
                Save & open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
